import React, { useState } from 'react';
import { Alert, Avatar, Box, Button, Chip, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { normalizeAvatarUrl } from '../../utils/avatar';
import { buildInvoiceDiscrepancy, displayMoney } from './invoiceDiscrepancy';

const readableDate = (date) => {
  if (!date) return null;
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
};

// Informational only: this notice never modifies an issued invoice or bypasses
// payment, QuickBooks, or invoice creation safeguards.
const InvoiceDiscrepancyNotice = ({
  balanceStatus,
  invoices = [],
  order,
  fallbackNotice = '',
  mismatchNotice = '',
  canManageInvoices = false,
  onOpenInvoice,
  compact = false,
}) => {
  const [expanded, setExpanded] = useState(false);
  const data = buildInvoiceDiscrepancy({ balanceStatus, invoices, order });

  if (!data) {
    if (!fallbackNotice && !mismatchNotice) return null;
    return (
      <Alert severity={mismatchNotice ? 'warning' : 'info'} sx={{ mb: 2, mt: compact ? 0 : 2.5 }}>
        {fallbackNotice}
        {mismatchNotice ? <Typography variant="body2" sx={{ mt: fallbackNotice ? 1 : 0 }}>{mismatchNotice}</Typography> : null}
      </Alert>
    );
  }

  const revisionDate = readableDate(data.refreshedAt);
  const invoiceCreatedDate = readableDate(data.invoiceCreatedAt);
  const invoiceDate = readableDate(data.invoiceDate);
  const avatarUrl = normalizeAvatarUrl(data.refreshedByAvatar);
  const revisionLabel = revisionDate
    ? `Regenerated ${revisionDate}${data.revisionNumber ? ` (revision ${data.revisionNumber})` : ''}`
    : data.revisionNumber ? `Revision ${data.revisionNumber}` : null;
  const invoicedLabel = invoiceCreatedDate
    ? `Created ${invoiceCreatedDate}`
    : invoiceDate ? `Dated ${invoiceDate}` : null;
  const hasInvoiceAction = canManageInvoices && data.invoiceId != null && typeof onOpenInvoice === 'function';

  const metric = (label, value, sublabel, Icon, accent = false) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0, flex: 1, p: { xs: 1, sm: 1.5 } }}>
      <Box sx={{
        width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        bgcolor: accent ? '#FCE8E9' : '#EDF1F6', color: accent ? '#CE3438' : '#5D6E89',
      }}>
        <Icon sx={{ fontSize: 19 }} />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="caption" sx={{ color: '#526179', display: 'block', fontSize: 11.5 }}>{label}</Typography>
        <Typography sx={{ fontWeight: 750, fontSize: { xs: 15, sm: 18 }, color: accent ? '#CC2732' : '#172132', lineHeight: 1.4 }}>
          {value}
        </Typography>
        {sublabel && <Typography variant="caption" sx={{ color: '#65748A', display: 'block', fontSize: 11, lineHeight: 1.5 }}>{sublabel}</Typography>}
      </Box>
    </Box>
  );

  return (
    <Box data-testid="invoice-discrepancy-notice" sx={{
      my: compact ? 1 : 2, p: { xs: 1.5, sm: compact ? 1.75 : 2.25 },
      bgcolor: '#FFFFFF', border: '1px solid #DFE6F1',
      borderRadius: 2.5, boxShadow: '0 3px 14px rgba(26, 42, 68, 0.035)',
    }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
        <Box sx={{ bgcolor: '#E8F0FF', borderRadius: '50%', p: 0.85, display: 'flex', flexShrink: 0, color: '#2867CC' }}>
          <InfoOutlinedIcon sx={{ fontSize: 21 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.9 }}>
            <Typography variant="body2" sx={{ fontWeight: 750, color: '#172132', fontSize: compact ? 15 : 16 }}>
              Invoice differs from current Sales Order
            </Typography>
            <Chip size="small" label={`+${displayMoney(data.differenceCents)}`}
              sx={{ height: 26, fontSize: 13, fontWeight: 750, bgcolor: '#FDE9EA', color: '#C92A35' }} />
          </Box>
          <Typography variant="body2" sx={{ mt: 0.7, color: '#465770', fontSize: 14, lineHeight: 1.65 }}>
            {data.invoiceId ? `Invoice #${data.invoiceId}` : 'Existing invoices'} is {displayMoney(data.coveredCents)}, while
            {' '}{data.salesOrderId ? `Sales Order #${data.salesOrderId}` : 'the current Sales Order'} is {displayMoney(data.salesOrderCents)}.
            {' '}{data.shipping ? 'The saved shipping comparison explains the difference.' : 'Review the saved records for details.'}
          </Typography>
        </Box>
        <Button size="small" color="inherit" aria-expanded={expanded} aria-controls="invoice-discrepancy-details"
          endIcon={expanded ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
          onClick={() => setExpanded((prev) => !prev)}
          sx={{ flexShrink: 0, fontSize: 13, textTransform: 'none', border: '1px solid #DDE4EE', borderRadius: 1.5, px: 1.3, color: '#293B55' }}>
          {expanded ? 'Hide details' : 'View details'}
        </Button>
      </Box>

      {expanded ? (
        <Box id="invoice-discrepancy-details" sx={{ mt: 1.8 }}>
          <Box sx={{
            display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, minmax(0, 1fr))' },
            bgcolor: '#F8FAFD', borderRadius: 2, border: '1px solid #F0F3F8',
            '& > div:not(:last-child)': { borderRight: { md: '1px solid #E5EAF1' } },
            '& > div:nth-of-type(odd)': { borderRight: { xs: '1px solid #E5EAF1', md: '1px solid #E5EAF1' } },
          }}>
            {metric(data.invoiceId ? `Invoice #${data.invoiceId}` : 'Invoice coverage', displayMoney(data.coveredCents), invoicedLabel, DescriptionOutlinedIcon)}
            {metric(data.salesOrderId ? `Sales Order #${data.salesOrderId}` : 'Current Sales Order', displayMoney(data.salesOrderCents), revisionLabel, DescriptionOutlinedIcon)}
            {metric('Difference', `+${displayMoney(data.differenceCents)}`, null, ArrowUpwardIcon, true)}
            {metric('Shipping comparison',
              data.shipping ? `${displayMoney(data.shipping.before.subtotalCents)} → ${displayMoney(data.shipping.after.subtotalCents)}` : 'Not available',
              data.shipping ? `+${displayMoney(data.shipping.after.subtotalCents - data.shipping.before.subtotalCents)} shipping · +${displayMoney(data.shipping.after.taxCents - data.shipping.before.taxCents)} tax` : 'Line-level evidence unavailable',
              LocalShippingOutlinedIcon)}
          </Box>
          {data.refreshedBy && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1.3, borderBottom: '1px solid #E7ECF3' }}>
              <Avatar src={avatarUrl || undefined} alt={data.refreshedBy} imgProps={{ referrerPolicy: 'no-referrer' }}
                sx={{ width: 30, height: 30, fontSize: 12, bgcolor: '#E9DDEA', color: '#683B6E' }}>
                {data.refreshedBy.split(/\s+/).filter(Boolean).slice(0, 2).map((v) => v.charAt(0)).join('').toUpperCase()}
              </Avatar>
              <Typography variant="caption" sx={{ color: '#526179', fontSize: 12 }}>
                Last regenerated by <strong>{data.refreshedBy}</strong>
                {revisionDate ? ` · ${revisionDate}` : ''}
                {data.revisionNumber ? ` · Revision ${data.revisionNumber}` : ''}
              </Typography>
            </Box>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mt: 1.5, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <Box sx={{ flex: 1, minWidth: 240 }}>
              {data.shipping ? (
                <Typography variant="body2" sx={{ color: '#526179', fontSize: 12, mb: 0.7 }}>
                  Shipping compared with {data.shipping.source}. The comparison explains the amount difference but not when the shipping was originally repriced.
                </Typography>
              ) : (
                <Typography variant="body2" sx={{ color: '#526179', fontSize: 12, mb: 0.7 }}>
                  No reliable line-level comparison is available from the saved records.
                </Typography>
              )}
              {data.shipping?.source === 'Order Cart' && (
                <Typography variant="caption" sx={{ color: '#65748A', display: 'block', mb: 0.6 }}>
                  The Order Cart is not a frozen invoice snapshot; confirm the revised shipping is billable.
                </Typography>
              )}
              <Typography variant="caption" sx={{ color: '#65748A', display: 'block' }}>
                Existing invoices remain unchanged. Check delivery, payments, and QuickBooks before revising or billing the difference.
              </Typography>
            </Box>
            {hasInvoiceAction && (
              <Button size="small" variant="outlined" color="inherit" onClick={() => onOpenInvoice(data.invoiceId)}
                sx={{ textTransform: 'none', whiteSpace: 'nowrap', borderColor: '#D5DDE9', color: '#213149' }}>
                Open invoice #{data.invoiceId}
              </Button>
            )}
          </Box>
        </Box>
      ) : (
        <Box sx={{ ml: { xs: 0, sm: 5.2 }, mt: 0.75 }}>
          {(invoicedLabel || revisionLabel) && (
            <Typography variant="caption" sx={{ color: '#66768D', fontSize: 12.5, display: 'block' }}>
              {invoicedLabel ? `Invoice ${invoicedLabel.toLowerCase()}` : ''}
              {invoicedLabel && revisionLabel ? ' · ' : ''}
              {revisionLabel ? `Sales Order ${revisionLabel.toLowerCase()}` : ''}
            </Typography>
          )}
          {data.shipping && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.85, flexWrap: 'wrap', mt: 0.8 }}>
              <LocalShippingOutlinedIcon sx={{ fontSize: 18, color: '#5C6C83' }} />
              <Typography variant="body2" sx={{ color: '#32445E', fontSize: 13.5 }}>
                Shipping: <strong>{displayMoney(data.shipping.before.subtotalCents)} → {displayMoney(data.shipping.after.subtotalCents)}</strong>
                <Box component="span" sx={{ color: '#68788E', ml: 0.75 }}>
                  (+{displayMoney(data.shipping.after.subtotalCents - data.shipping.before.subtotalCents)} shipping, +{displayMoney(data.shipping.after.taxCents - data.shipping.before.taxCents)} tax)
                </Box>
              </Typography>
            </Box>
          )}
          {data.refreshedBy && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7, mt: 0.85 }}>
              <Avatar src={avatarUrl || undefined} alt={data.refreshedBy} imgProps={{ referrerPolicy: 'no-referrer' }}
                sx={{ width: 28, height: 28, fontSize: 11, bgcolor: '#E9DDEA', color: '#683B6E' }}>
                {data.refreshedBy.split(/\s+/).filter(Boolean).slice(0, 2).map((v) => v.charAt(0)).join('').toUpperCase()}
              </Avatar>
              <Typography variant="caption" sx={{ color: '#66768D', fontSize: 12.5 }}>
                Last regenerated by {data.refreshedBy}{revisionDate ? ` · ${revisionDate}` : ''}
                {data.revisionNumber ? ` · Revision ${data.revisionNumber}` : ''}
              </Typography>
            </Box>
          )}
        </Box>
      )}
      {mismatchNotice && <Alert severity="warning" sx={{ mt: 1.2, py: 0 }}>{mismatchNotice}</Alert>}
    </Box>
  );
};

export default InvoiceDiscrepancyNotice;
